// Translation Service for Meal Plans
// Handles translation with caching to reduce API calls

// Cache Configuration
const CACHE_PREFIX = 'meal_translation';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Stable djb2-style 32-bit string hash. Used to fingerprint meal-plan content
 * so the translation cache key changes whenever anything translatable changes
 * (ingredient names, portion sizes, meal titles, notes, …).
 */
const hashString = (str) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  // Force unsigned 32-bit, base36 for compactness
  return (hash >>> 0).toString(36);
};

/**
 * Build a stable, content-sensitive fingerprint of the parts of a meal that
 * actually feed into translation. Anything that, when changed, must invalidate
 * the cached Hebrew version belongs here.
 */
const fingerprintMeal = (m) => ({
  meal: m?.meal || '',
  main: {
    title: m?.main?.meal_title || m?.main?.meal_name || m?.main?.title || '',
    ingredients: (m?.main?.ingredients || []).map((ing) => ({
      item: ing?.item || ing?.name || '',
      portion: ing?.['portionSI(gram)'] ?? ing?.portion ?? 0,
      household: ing?.household_measure || '',
    })),
  },
  alternative: {
    title: m?.alternative?.meal_title || m?.alternative?.meal_name || '',
    ingredients: (m?.alternative?.ingredients || []).map((ing) => ({
      item: ing?.item || ing?.name || '',
      portion: ing?.['portionSI(gram)'] ?? ing?.portion ?? 0,
      household: ing?.household_measure || '',
    })),
  },
});

/**
 * Generate cache key from menu structure.
 *
 * IMPORTANT: the key must change whenever ANY translatable field changes,
 * otherwise editing a meal plan (e.g. adding an ingredient) shows stale
 * cached Hebrew text until the user clears site data.
 */
const generateCacheKey = (menu, targetLang) => {
  const keyData = {
    targetLang,
    note: menu?.note || '',
    meals: (menu?.meals || []).map(fingerprintMeal),
  };
  const keyString = JSON.stringify(keyData);
  return `${CACHE_PREFIX}_${targetLang}_${hashString(keyString)}`;
};

/**
 * Generate cache key from meal plan structure.
 * See note on generateCacheKey – this must be fully content-sensitive.
 */
const generateMealPlanCacheKey = (mealPlan, targetLang) => {
  const keyData = {
    targetLang,
    note: mealPlan?.note || '',
    meals: (mealPlan?.meals || []).map(fingerprintMeal),
  };
  const keyString = JSON.stringify(keyData);
  return `${CACHE_PREFIX}_mealplan_${targetLang}_${hashString(keyString)}`;
};

/**
 * Get cached translation
 */
const getCachedTranslation = (cacheKey) => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

/**
 * Cache translation
 */
const cacheTranslation = (cacheKey, data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error caching translation:', error);
    // If localStorage is full, try to clear old translations
    clearOldTranslations();
    // Retry caching
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (retryError) {
      console.error('Failed to cache translation after cleanup:', retryError);
    }
  }
};

/**
 * Clear old translations from cache
 */
const clearOldTranslations = () => {
  try {
    const keysToRemove = [];
    const now = Date.now();
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (cached && cached.timestamp) {
            if (now - cached.timestamp > CACHE_EXPIRY_MS) {
              keysToRemove.push(key);
            }
          }
        } catch (e) {
          // Invalid cache entry, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`Cleared ${keysToRemove.length} old translation cache entries`);
  } catch (error) {
    console.error('Error clearing old translations:', error);
  }
};

/**
 * Main translation function
 * @param {Object} menu - Menu object with meals array
 * @param {string} targetLang - Target language code ('he' for Hebrew, 'en' for English)
 * @returns {Promise<Object>} Translated menu
 */
export const translateMenu = async (menu, targetLang = 'he') => {
  // Early return if English or empty menu
  if (targetLang === 'en' || !menu || !menu.meals || menu.meals.length === 0) {
    return menu;
  }

  try {
    // 1. Generate cache key based on menu content
    const cacheKey = generateCacheKey(menu, targetLang);
    
    // 2. Check cache first (24-hour expiry)
    const cachedTranslation = getCachedTranslation(cacheKey);
    if (cachedTranslation) {
      console.log('✅ Using cached menu translation');
      return cachedTranslation;
    }

    // 3. Prepare menu for translation
    const menuToTranslate = {
      meals: menu.meals || [],
      note: menu.note || ''
    };

    // 4. Call external translation API
    const translateApiUrl = process.env.REACT_APP_TRANSLATE_API_URL || 
                           'https://newdietitianweb-615263253386.europe-west3.run.app/api/translate';
    
    const response = await fetch(translateApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        menu: menuToTranslate, 
        targetLang 
      }),
    });

    if (!response.ok) {
      throw new Error(`Translation failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    // 5. Cache the successful translation
    cacheTranslation(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error translating menu:', error);
    
    // Try fallback cache
    const fallbackTranslation = getCachedTranslation(`${CACHE_PREFIX}_${targetLang}_fallback`);
    if (fallbackTranslation) {
      console.log('✅ Using fallback cached translation');
      return fallbackTranslation;
    }
    
    // Return original menu if translation fails
    console.warn('⚠️ Translation failed, returning original menu');
    return menu;
  }
};

/**
 * Translate meal plan (ingredients and meal titles, but keep meal.meal in English)
 * @param {Object} mealPlan - Meal plan object with meals array
 * @param {string} targetLang - Target language code ('he' for Hebrew, 'en' for English)
 * @returns {Promise<Object>} Translated meal plan
 */
export const translateMealPlan = async (mealPlan, targetLang = 'he') => {
  // Early return if English or empty meal plan
  if (targetLang === 'en' || !mealPlan || !mealPlan.meals || mealPlan.meals.length === 0) {
    return mealPlan;
  }

  try {
    // 1. Generate cache key based on meal plan content
    const cacheKey = generateMealPlanCacheKey(mealPlan, targetLang);
    
    // 2. Check cache first (24-hour expiry)
    const cachedTranslation = getCachedTranslation(cacheKey);
    if (cachedTranslation) {
      console.log('✅ Using cached meal plan translation');
      return cachedTranslation;
    }

    // 3. Prepare meal plan for translation
    // We need to translate:
    // - meal.main.meal_title, meal.main.meal_name, meal.main.title
    // - meal.alternative.meal_title, meal.alternative.meal_name
    // - ingredient.item and ingredient.name
    // But keep meal.meal in English
    const mealPlanToTranslate = {
      meals: mealPlan.meals || [],
      totals: mealPlan.totals || null,
      note: mealPlan.note || ''
    };

    // 4. Call external translation API
    const translateApiUrl = process.env.REACT_APP_TRANSLATE_API_URL || 
                           'https://newdietitianweb-615263253386.europe-west3.run.app/api/translate';
    
    const response = await fetch(translateApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        mealPlan: mealPlanToTranslate, 
        targetLang 
      }),
    });

    if (!response.ok) {
      throw new Error(`Translation failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    // 5. Validate translation result
    if (!result || !result.meals || !Array.isArray(result.meals)) {
      console.error('❌ Invalid translation response structure:', result);
      throw new Error('Translation API returned invalid structure');
    }
    
    // 6. Ensure meal.meal stays in English (don't translate meal type names)
    if (result.meals && mealPlan.meals) {
      result.meals.forEach((translatedMeal, index) => {
        if (mealPlan.meals[index]?.meal) {
          // Keep original English meal type
          translatedMeal.meal = mealPlan.meals[index].meal;
        }
      });
    }
    
    // 7. Ensure totals are preserved
    if (!result.totals && mealPlan.totals) {
      result.totals = mealPlan.totals;
    }
    
    // 8. Cache the successful translation
    cacheTranslation(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error translating meal plan:', error);
    
    // Try fallback cache
    const fallbackTranslation = getCachedTranslation(`${CACHE_PREFIX}_mealplan_${targetLang}_fallback`);
    if (fallbackTranslation) {
      console.log('✅ Using fallback cached meal plan translation');
      return fallbackTranslation;
    }
    
    // Return original meal plan if translation fails
    console.warn('⚠️ Meal plan translation failed, returning original meal plan');
    return mealPlan;
  }
};

